import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import { Window, type WindowProps } from "./Window";
import "./FloatingWindow.scss";

type FloatingWindowProps = Omit<WindowProps, "eyebrow">;
type DragState = {
  pointerID: number;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
  baseLeft: number;
  baseRight: number;
  baseTop: number;
  baseBottom: number;
};

let nextFloatingWindowZIndex = 81;

export function FloatingWindow({
  className = "",
  onClose,
  ...props
}: FloatingWindowProps) {
  const dragRef = useRef<DragState | null>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [zIndex, setZIndex] = useState(() => nextFloatingWindowZIndex++);

  const bringToFront = () => {
    setZIndex(nextFloatingWindowZIndex++);
  };

  useEffect(() => {
    const move = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerID) return;

      const nextX = drag.originX + event.clientX - drag.startX;
      const nextY = drag.originY + event.clientY - drag.startY;
      const minX = 24 - drag.baseLeft;
      const maxX = window.innerWidth - 24 - drag.baseRight;
      const minY = 24 - drag.baseTop;
      const maxY = window.innerHeight - 24 - drag.baseBottom;

      setPosition({
        x: Math.min(maxX, Math.max(minX, nextX)),
        y: Math.min(maxY, Math.max(minY, nextY)),
      });
    };
    const stop = (event: PointerEvent) => {
      if (dragRef.current?.pointerID === event.pointerId) {
        dragRef.current = null;
      }
    };

    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", stop);
    document.addEventListener("pointercancel", stop);
    return () => {
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", stop);
      document.removeEventListener("pointercancel", stop);
    };
  }, []);

  const startDrag = (event: ReactPointerEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).closest("button")) return;
    event.preventDefault();
    const rect = event.currentTarget.parentElement?.getBoundingClientRect();
    if (!rect) return;
    dragRef.current = {
      pointerID: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
      baseLeft: rect.left - position.x,
      baseRight: rect.right - position.x,
      baseTop: rect.top - position.y,
      baseBottom: rect.bottom - position.y,
    };
  };

  const floatingWindow = (
    <Window
      as="dialog"
      className={`floating-window ${className}`.trim()}
      closedby="closerequest"
      modal={false}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
      onPointerDown={bringToFront}
      onTopbarPointerDown={startDrag}
      open
      style={{
        transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))`,
        zIndex,
      }}
      {...props}
    />
  );

  return typeof document === "undefined"
    ? floatingWindow
    : createPortal(floatingWindow, document.body);
}
